import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { EcointelLottie } from './EcoIntelLoader';

// Phase 0: the SubscriptionGate is no longer a blanket paywall. The freemium
// model means every authenticated user reaches the app shell and the dashboard;
// per-feature gating happens inside each surface via `hasFeature(...)` in
// `constants/featureGates.js`.
//
// This component is retained for backwards-compatible routing (App.js wraps a
// number of routes in <SubscriptionGate>...). It now waits for the user doc to
// load (so consuming components see consistent state from the first render),
// surfaces lightweight status banners for `past_due` / `cancelled` subscriptions,
// and otherwise passes children straight through.
//
// If the gate needs to become a blocker again for a specific surface, prefer
// adding a feature gate at the surface level rather than re-enabling a blanket
// block here.

export default function SubscriptionGate({ children }) {
  const [status, setStatus] = useState('loading');
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const u = auth.currentUser;
    if (!u) { setStatus('ready'); return; }

    const unsub = onSnapshot(
      doc(db, 'users', u.uid),
      (snap) => {
        if (snap.exists()) setUserData(snap.data());
        setStatus('ready');
      },
      () => {
        // On error we still let the app render rather than holding the user on
        // a loading screen. Downstream surfaces gate on their own.
        setStatus('ready');
      }
    );

    return () => unsub();
  }, []);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center h-full min-h-[50vh] bg-slate-50">
        <EcointelLottie message="Loading your account..." lottiePixelWidth={120} lottiePixelHeight={150} />
      </div>
    );
  }

  const showCancelledBanner = userData && !userData.subscriptionOverride && userData.subscriptionStatus === 'cancelled';
  const showPastDueBanner = userData && userData.subscriptionStatus === 'past_due';

  return (
    <>
      {showPastDueBanner && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-sm text-red-700">
          Your last payment failed. Please update your payment method to keep your subscription active.
        </div>
      )}
      {showCancelledBanner && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-700">
          Your subscription has been cancelled. You can resubscribe at any time from your account profile.
        </div>
      )}
      {children}
    </>
  );
}
